import { Box, Stack, Typography, alpha, useTheme } from '@mui/material';
import type { CoverageStatus, HansAckerReport } from './types';

const TONES = { found: '#ef4444', ok: '#22c55e', muted: '#64748b', warn: '#f59e0b' };
const ICONS: Record<CoverageStatus, { glyph: string; color: string }> = {
  found: { glyph: '!', color: '#ef4444' },
  clean: { glyph: '\u2713', color: '#22c55e' },
  na: { glyph: '\u2013', color: '#64748b' },
};

export default function CoverageSection({ coverage }: { coverage: HansAckerReport['coverage'] }) {
  const theme = useTheme();
  return (
    <Box id="hans-acker-coverage" sx={{ scrollMarginTop: 72, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 3, p: 2.5 }}>
      <Stack direction="row" alignItems="baseline" spacing={1.5} sx={{ mb: 2 }}>
        <Typography sx={{ fontSize: 16, fontWeight: 700 }}>What was checked</Typography>
        <Typography sx={{ ml: 'auto', fontSize: 12, color: 'text.disabled' }}>
          {coverage.surfacesReviewed} surfaces reviewed across {coverage.riskAreas} risk areas
        </Typography>
      </Stack>

      <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1, mb: 2 }}>
        {coverage.chips.map((c) => (
          <Box key={c.label} sx={{ fontSize: 12.5, fontWeight: 600, px: 1.5, py: 0.75, borderRadius: 2, border: '1px solid', borderColor: 'divider', bgcolor: alpha(theme.palette.text.primary, 0.03), color: TONES[c.tone] }}>
            {c.label}
          </Box>
        ))}
      </Stack>

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 1.25 }}>
        {coverage.items.map((item) => {
          const icon = ICONS[item.status];
          return (
            <Stack key={item.name} direction="row" spacing={1.25} alignItems="flex-start" sx={{ p: 1.5, borderRadius: 2, border: '1px solid', borderColor: 'divider', bgcolor: alpha(theme.palette.text.primary, 0.025) }}>
              <Box sx={{ width: 22, height: 22, borderRadius: '50%', flexShrink: 0, display: 'grid', placeItems: 'center', fontSize: 12, fontWeight: 800, color: icon.color, bgcolor: alpha(icon.color, 0.16) }}>{icon.glyph}</Box>
              <Box>
                <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{item.name}</Typography>
                <Typography sx={{ fontSize: 11.5, color: 'text.secondary', mt: 0.25, lineHeight: 1.4 }}>{item.detail}</Typography>
              </Box>
            </Stack>
          );
        })}
      </Box>

      <Box sx={{ mt: 2, p: 1.75, borderRadius: 2, border: '1px solid', borderColor: alpha('#f59e0b', 0.3), bgcolor: alpha('#f59e0b', 0.07) }}>
        <Typography sx={{ fontSize: 10.5, fontWeight: 800, letterSpacing: 0.7, textTransform: 'uppercase', color: '#d97706', mb: 0.75 }}>Needs follow-up</Typography>
        <Box component="ul" sx={{ m: 0, pl: 2.25 }}>
          {coverage.followUp.map((t) => <Typography key={t} component="li" sx={{ fontSize: 12.5, lineHeight: 1.55, color: 'text.secondary' }}>{t}</Typography>)}
        </Box>
      </Box>
    </Box>
  );
}
